import React from 'react'
import { Route, Redirect, RouteProps } from 'react-router-dom'
import { includes } from 'lodash'
import { staticLinks } from 'shared/linkBuilder'
import ErrorBoundary from '../common/error/ErrorBoundary'
import { useApolloNetworkStatus } from '../../graphql/apolloClient'
import { adminAndManagerOrganisationRoles } from 'types/Organisation'
import { usePublicApiSession } from 'front-end/src/public-api/hooks/usePublicApiSession'
import OfflinePopup from '../common/offline/OfflinePopup'
import { OnlineStatusProvider } from '../../public-api/hooks/useOnlineStatus'
import { WithRenderedComponent } from 'front-end/src/components/WithRenderedComponent'
import { useRenderedComponentContext } from 'front-end/src/components/RenderedComponentContext'

export const NetworkStatusContext = React.createContext({
  numPendingMutations: 0,
  numPendingQueries: 0
})

const NetworkStatusWrapper = ({ children }) => {
  const status = useApolloNetworkStatus()

  return (
    <NetworkStatusContext.Provider value={status}>
      {children}
    </NetworkStatusContext.Provider>
  )
}

type RouteWithComponentProp = {
  component: RouteProps['component']
}

type RouteWithRenderProp = {
  render: RouteProps['render']
  id: string
}

type PrivateRouteProps = RouteProps & {
  adminOnly?: boolean
  featureDisabled?: boolean
} & (RouteWithComponentProp | RouteWithRenderProp)

const WrappedRoute = ({ component, render, id, ...rest }) => {
  const { setRenderedComponentName } = useRenderedComponentContext()
  const wrappedComponent = React.useMemo(() => {
    return WithRenderedComponent(
      component,
      render,
      setRenderedComponentName,
      id
    )
  }, [component, render, id])
  return <Route component={wrappedComponent} {...rest} />
}

// TODO make sure we can get the params inference working properly for this. As it is we always get routeProps as object with unknown keys
const PrivateRoute = ({
  adminOnly = false,
  featureDisabled = false,
  ...props
}: PrivateRouteProps) => {
  const { session } = usePublicApiSession()

  const isAdminOrManager = includes(
    adminAndManagerOrganisationRoles,
    session?.user.organisation_role
  )

  if (featureDisabled || (adminOnly && !isAdminOrManager)) {
    return <Redirect to={staticLinks.home} />
  }

  return (
    <OnlineStatusProvider>
      <NetworkStatusWrapper>
        <ErrorBoundary>
          {/*// @ts-ignore*/}
          <WrappedRoute {...props} />
        </ErrorBoundary>
        <OfflinePopup />
      </NetworkStatusWrapper>
    </OnlineStatusProvider>
  )
}

export default PrivateRoute
